import React from 'react';
import usePayments from '../../../hooks/api/usePayments';
import { getIntl } from '../../../utils/intl';
import RegistrationCodePromptContents from './RegistrationCodePromptContents';
import ActiveRegistrationCodeAlert from './ActiveRegistrationCodeAlert';
import PendingUrlRegistrationCodeAlert from './PendingUrlRegistrationCodeAlert';
import Stack, { contentStackSpacing } from '../../v2BaseComponents/Stack';
import Text from '../../v2BaseComponents/Text';
import Button from '../../v2BaseComponents/Button';
import { StandardPrompt } from '../../v2BaseComponents/Prompt';
import { useValidatedUrlRegistrationCode } from '../../../hooks/useUrlAppParams';
import { Platform } from 'react-native';

const intl = getIntl('AccountScreenRegistrationCodeInput', {
    header: 'Do you have a coupon code?',
    message: 'If given a coupon code, add it to your account ',
    addCode: 'Have a Coupon Code?',
    haveARegistrationCode: 'Coupon Code',
    couponCodeContent:
        'Coupon codes are used to unlock premium features and content. If you have a coupon code, enter it here.',
    addCouponCode: 'Add Coupon Code',
    enterCode: 'Enter Coupon Code',
});

export type AccountScreenRegistrationCodeInputOwnProps = {};

export type AccountScreenRegistrationCodeInputProps = AccountScreenRegistrationCodeInputOwnProps;

/**
 * Show the registration code input on the account landing screen.  If this is a new user, we want to make
 * the registration/payment as easy and obvious as possible.  If this is a new user with no products or registration code
 * we want to make the registration code input as obvious as possible.  If the user does not fit this criteria, we don't
 * want to show anything and clutter the UI
 */
const AccountScreenRegistrationCodeInput: React.FC<AccountScreenRegistrationCodeInputProps> = () => {
    const [isPromptOpen, setIsPromptOpen] = React.useState(false);
    const { paymentStatus, myRegistrationCodeStatus, isLoadingPaymentStatus, isLoadingRegistrationCodeStatus } =
        usePayments({});
    const validateUrlRegistrationCodeResult = useValidatedUrlRegistrationCode();
    const hasPresentedPrompt = React.useRef(false);

    // loading state
    if (
        !paymentStatus ||
        !myRegistrationCodeStatus ||
        isLoadingPaymentStatus ||
        isLoadingRegistrationCodeStatus ||
        validateUrlRegistrationCodeResult.urlRegistrationCodeStatus === 'validating'
    )
        return null;

    // user has already made a purchase
    if (paymentStatus.products.length) return null;

    if (myRegistrationCodeStatus.status.type !== 'no-registration-code') {
        /**
         * If the user has a registration code but has not just configured it, show nothing
         */
        if (!hasPresentedPrompt.current) {
            return null;
        } else {
            return <ActiveRegistrationCodeAlert status={myRegistrationCodeStatus.status} disableRemoveCode />;
        }
    } else if (validateUrlRegistrationCodeResult.urlRegistrationCodeStatus === 'valid') {
        /**
         * If the user has a valid registration code in the URL, show the prompt
         */
        return (
            <PendingUrlRegistrationCodeAlert
                status={validateUrlRegistrationCodeResult.validationResult}
                code={validateUrlRegistrationCodeResult.code}
            />
        );
    } else if (Platform.OS === 'ios') {
        /**
         * If the user is on iOS and has no registration code, show nothing.  iOS does not allow for
         * registration codes to be added in the app
         */
        return null;
    } else {
        /**
         * If the user has no registration code, allow them to add one
         */
        hasPresentedPrompt.current = true;

        return (
            <>
                <Stack vertical align="center" space={contentStackSpacing} fullWidth>
                    <Text variant="body-1">{intl('couponCodeContent')}</Text>
                    <Button
                        onPress={() => setIsPromptOpen(true)}
                        testID="registration-code-button"
                        icon="PlusCircle"
                        title={intl('addCouponCode')}
                        size="lg"
                        variant="secondary"
                    />
                </Stack>
                <StandardPrompt
                    isOpen={isPromptOpen}
                    setIsOpen={(isOpen) => setIsPromptOpen(isOpen)}
                    title={intl('enterCode')}
                >
                    <RegistrationCodePromptContents onClosePrompt={() => setIsPromptOpen(false)} />
                </StandardPrompt>
            </>
        );
    }
};

export default React.memo(AccountScreenRegistrationCodeInput);
